import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import useNavigation from '@Navigation/useNavigation';
import transformUrl from '@Navigation/transformUrl';
import * as PageProductStore from '@Stores/PageProduct';
import * as Configurator from '@Stores/Configurator';
import ApiProduct from '@Api/Product';
import { useRequest } from '@Contexts/Request';
import useKeys from './useKeys';
import filterFabricsByCollections from './helpers/filterFabricsByCollections';

import type { ConfiguratorData, ConstructorValueData } from '@Pages/PageProduct';
import type { FilterResultData } from '@Api/Configurator/getFilterResult';

function useConfigurator(
  id: number,
  initialConfigurator: ConfiguratorData,
  selectedPv: ConstructorValueData[],
) {
  const keys = useKeys(id, selectedPv);
  const { region } = useRequest();
  const queryClient = useQueryClient();
  const {
    product: { modelId },
  } = PageProductStore.useData();
  const initialRegion = useRef(region);
  const { navigate } = useNavigation();

  const initialFormatted = useMemo(() => {
    return {
      ...initialConfigurator,
      fabricCollections: filterFabricsByCollections(
        initialConfigurator.fabricCollections,
        initialConfigurator.parameterValues,
      ),
    };
  }, [initialConfigurator]);

  const enabled = useMemo(() => {
    const allSelectedPv = initialConfigurator.parameterValues.filter((pv) => pv.selected);
    const allPvSelected = allSelectedPv.every((pv) =>
      selectedPv.some((spv) => spv.value === pv.value),
    );

    const regionChanged = initialRegion.current !== region;

    return !allPvSelected || regionChanged;
  }, [initialConfigurator.parameterValues, selectedPv, region]);

  const select = useCallback(
    (data: ConfiguratorData) => {
      if (!data) return initialFormatted;

      const filters = queryClient.getQueryData<FilterResultData>([
        'selected-filters',
        modelId,
        region,
      ]);
      const fabrics: ConstructorValueData[] = [];
      const parameters: ConstructorValueData[] = [];
      const inStock = +filters?.filters.parameters.in_stock?.default === 1;

      data.parameterValues.forEach((item) => {
        if (item.type === 'fabric') {
          fabrics.push(item);
        } else {
          // Запрещаем выбор недоступных опций только для товаров со склада
          parameters.push({
            ...item,
            calcDisabled: inStock ? item.disabled : false,
          });
        }
      });

      const parameterValues = [
        ...parameters,
        ...fabrics.map((item) => ({
          ...item,
          hidden: filters
            ? !(filters.parameterValues || []).some(
                (pv) =>
                  pv.value === item.value && pv.tags?.collectionId === item.tags?.collectionId,
              )
            : false,
        })),
      ];

      return {
        ...data,
        fabricFilters: filters?.filters || data.fabricFilters,
        parameterValues,
        fabricCollections: filterFabricsByCollections(data.fabricCollections, parameterValues),
        countFabrics: filters?.parameterValues.length || data.countFabrics,
      };
    },
    [initialFormatted, modelId, queryClient, region],
  );

  const query = useQuery({
    queryKey: keys,
    queryFn: () => {
      return ApiProduct.getConfigurator({
        shopProductId: id,
        parameterValues: selectedPv,
      });
    },
    select,
    initialData: initialConfigurator ? initialFormatted : undefined,
    placeholderData: (previousData) => previousData,
    enabled,
  });

  useEffect(() => {
    if (!query.data) return;

    const data = select(query.data);

    initialRegion.current = region;

    navigate(transformUrl({ url: `${data.configuration.url}` }), {
      preventScrollReset: true,
      replace: true,
    });

    const initialValues = new Set(
      initialConfigurator.parameterValues.filter((pv) => pv.selected).map((pv) => pv.value),
    );
    const currentValues = data.parameterValues.filter((pv) => pv.selected);

    if (!currentValues.every((item) => initialValues.has(item.value))) {
      Configurator.changePanelView(true);
      setTimeout(() => {
        Configurator.changePanelView(false);
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data]);

  return query;
}

export default useConfigurator;
